import React, { useEffect, useState } from "react";
import useAxios from "../../utils/useAxios";
import { showModal, hideModal } from "../../utils/modalUtility";
import { formatIndianCurrency } from "../../utils/currencyFormat";
import { useAppContext } from "../../context/AppContext";
import { useNavigate } from "react-router-dom";

// Helper function to format stored UTC date to user's Local Date & Time string
const formatUtcToLocalDate = (utcDateString) => {
  if (!utcDateString) return "-";
  try {
    let isoString = utcDateString;
    if (!utcDateString.includes("Z") && !utcDateString.includes("+")) {
      isoString = utcDateString.replace(" ", "T") + "Z";
    }
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return utcDateString;

    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch (e) {
    return utcDateString;
  }
};

// Helper to get local date-time string for datetime-local input (YYYY-MM-DDTHH:mm)
const getLocalDatetimeInputValue = (date = new Date()) => {
  const pad = (n) => String(n).padStart(2, "0");
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const DailyRegistrations = () => {
  const axiosAPI = useAxios();
  const navigate = useNavigate();
  const { state } = useAppContext();
  const role = state?.user?.role;

  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);

  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Form State matching Donations.js pattern
  const [formData, setFormData] = useState({
    amount: "",
    mode: "online",
    no_of_donors: "",
    created_at: getLocalDatetimeInputValue(),
  });

  // Filters State matching Donations.js pattern
  const [filters, setFilters] = useState({
    mode: "",
    from_date: "",
    to_date: "",
  });

  // Check admin role
  useEffect(() => {
    const roleFromStorage = JSON.parse(
      localStorage.getItem("user_role") || "null",
    );
    const activeRole = role || roleFromStorage;
    if (activeRole && activeRole !== "admin") {
      navigate("/user/dashboard");
    }
  }, [role, navigate]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      mode: "",
      from_date: "",
      to_date: "",
    });
    setPage(1);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setFormData({
      amount: "",
      mode: "online",
      no_of_donors: "",
      created_at: getLocalDatetimeInputValue(),
    });
    setIsEditMode(false);
    setCurrentId(null);
  };

  const openAddModal = () => {
    resetForm();
    showModal("dailyRegistrationModal");
  };

  const openEditModal = (item) => {
    setIsEditMode(true);
    setCurrentId(item.id);

    let localDateValue = getLocalDatetimeInputValue();
    if (item.created_at) {
      let iso = item.created_at;
      if (!iso.includes("Z") && !iso.includes("+")) {
        iso = iso.replace(" ", "T") + "Z";
      }
      const d = new Date(iso);
      if (!isNaN(d.getTime())) {
        localDateValue = getLocalDatetimeInputValue(d);
      }
    }

    setFormData({
      amount: item.amount,
      mode: item.mode?.toLowerCase() || "online",
      no_of_donors: item.no_of_donors,
      created_at: localDateValue,
    });

    showModal("dailyRegistrationModal");
  };

  const closeModal = () => {
    hideModal("dailyRegistrationModal");
    resetForm();
  };

  // Fetch registrations
  const fetchRegistrations = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", 20);
      if (filters.mode) params.append("mode", filters.mode);
      if (filters.from_date) params.append("from_date", filters.from_date);
      if (filters.to_date) params.append("to_date", filters.to_date);

      const response = await axiosAPI.get(
        `/api/daily-registration/get-all?${params.toString()}`,
      );

      if (response.data?.success) {
        setRecords(response.data.data || []);
        setTotalPages(response.data.pagination?.totalPages || 1);
      }
    } catch (err) {
      console.error("Failed to fetch daily registrations:", err);
      if (window.notifyMessage) {
        window.notifyMessage(
          "Error",
          err.response?.data?.message || "Failed to load registrations.",
          "danger",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRegistrations();
    // eslint-disable-next-line
  }, [page]);

  const handleFilterClick = () => {
    setPage(1);
    fetchRegistrations();
  };

  const handlePageClick = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setPage(pageNumber);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.amount || !formData.mode || !formData.no_of_donors) {
      if (window.notifyMessage) {
        window.notifyMessage(
          "Error",
          "Please fill all required fields.",
          "danger",
        );
      }
      return;
    }

    setIsSubmitting(true);

    try {
      // Convert selected local date-time to ISO UTC string for backend
      const selectedUtc = new Date(formData.created_at).toISOString();

      const payload = {
        amount: parseFloat(formData.amount),
        mode: formData.mode.toLowerCase(),
        no_of_donors: parseInt(formData.no_of_donors, 10),
        date: selectedUtc,
      };

      if (isEditMode) {
        await axiosAPI.put(
          `/api/daily-registration/update/${currentId}`,
          payload,
        );
        if (window.notifyMessage) {
          window.notifyMessage(
            "Success",
            "Daily registration updated successfully!",
            "success",
          );
        }
      } else {
        await axiosAPI.post("/api/daily-registration/add", payload);
        if (window.notifyMessage) {
          window.notifyMessage(
            "Success",
            "Daily registration added successfully!",
            "success",
          );
        }
      }

      closeModal();
      fetchRegistrations();
    } catch (err) {
      console.error("Error saving daily registration:", err);
      if (window.notifyMessage) {
        window.notifyMessage(
          "Error",
          err.response?.data?.message || "Failed to save daily registration.",
          "danger",
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this record?")) {
      return;
    }

    try {
      await axiosAPI.delete(`/api/daily-registration/delete/${id}`);
      if (window.notifyMessage) {
        window.notifyMessage(
          "Success",
          "Daily registration deleted successfully.",
          "success",
        );
      }
      fetchRegistrations();
    } catch (err) {
      console.error("Error deleting registration:", err);
      if (window.notifyMessage) {
        window.notifyMessage(
          "Error",
          err.response?.data?.message || "Failed to delete record.",
          "danger",
        );
      }
    }
  };

  return (
    <div className="container">
      <div className="page-inner">
        {/* Full-screen Loading Overlay matching Donations.js */}
        {loading && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              className="spinner-border text-success"
              role="status"
              style={{ width: "3rem", height: "3rem" }}
            >
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}

        {/* Modal matching Donations.js modal structure */}
        <div
          className="modal fade"
          id="dailyRegistrationModal"
          tabIndex="-1"
          aria-labelledby="dailyRegistrationModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="dailyRegistrationModalLabel">
                  {isEditMode
                    ? "Edit Daily Registration"
                    : "Add Daily Registration"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={closeModal}
                ></button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row">
                    {/* Mode (Online / Offline) */}
                    <div className="mb-3 col-md-6">
                      <label htmlFor="mode" className="form-label">
                        Mode
                      </label>
                      <select
                        className="form-select"
                        id="mode"
                        name="mode"
                        required
                        value={formData.mode}
                        onChange={handleChange}
                      >
                        <option value="online">Online</option>
                        <option value="offline">Offline</option>
                      </select>
                    </div>

                    {/* No of Donors */}
                    <div className="mb-3 col-md-6">
                      <label htmlFor="no_of_donors" className="form-label">
                        No of Donors
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="no_of_donors"
                        name="no_of_donors"
                        required
                        placeholder="Enter number of donors"
                        value={formData.no_of_donors}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="row">
                    {/* Amount */}
                    <div className="mb-3 col-md-6">
                      <label htmlFor="amount" className="form-label">
                        Amount
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="amount"
                        required
                        name="amount"
                        placeholder="Enter Amount"
                        value={formData.amount}
                        onChange={handleChange}
                      />
                    </div>

                    {/* Date & Time */}
                    <div className="mb-3 col-md-6">
                      <label htmlFor="created_at" className="form-label">
                        Date &amp; Time
                      </label>
                      <input
                        type="datetime-local"
                        className="form-control"
                        id="created_at"
                        name="created_at"
                        required
                        value={formData.created_at}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    data-bs-dismiss="modal"
                    onClick={closeModal}
                  >
                    Close
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        {isEditMode ? "Updating..." : "Adding..."}
                      </>
                    ) : isEditMode ? (
                      "Update Record"
                    ) : (
                      "Add Record"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Main Content Table Card matching Donations.js */}
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header d-flex flex-wrap justify-content-between align-items-end gap-3">
                {/* Left: Title */}
                <h4 className="card-title mb-0">
                  All Daily Registrations ({records.length})
                </h4>

                {/* Right: All filters including buttons */}
                <div className="d-flex flex-wrap align-items-end gap-3 ms-auto">
                  {/* Mode Filter */}
                  <div className="d-flex flex-column">
                    <label htmlFor="filterMode" className="form-label mb-1">
                      Mode
                    </label>
                    <select
                      id="filterMode"
                      className="form-select"
                      style={{ width: "140px" }}
                      value={filters.mode}
                      onChange={(e) =>
                        handleFilterChange("mode", e.target.value)
                      }
                    >
                      <option value="">All Modes</option>
                      <option value="online">Online</option>
                      <option value="offline">Offline</option>
                    </select>
                  </div>

                  {/* From Date */}
                  <div className="d-flex flex-column">
                    <label htmlFor="fromDate" className="form-label mb-1">
                      From
                    </label>
                    <input
                      id="fromDate"
                      type="date"
                      className="form-control"
                      value={filters.from_date || ""}
                      onChange={(e) =>
                        handleFilterChange("from_date", e.target.value)
                      }
                    />
                  </div>

                  {/* To Date */}
                  <div className="d-flex flex-column">
                    <label htmlFor="toDate" className="form-label mb-1">
                      To
                    </label>
                    <input
                      id="toDate"
                      type="date"
                      className="form-control"
                      value={filters.to_date || ""}
                      onChange={(e) =>
                        handleFilterChange("to_date", e.target.value)
                      }
                    />
                  </div>

                  {/* Filter Button */}
                  <button
                    className="btn btn-primary rounded-pill px-4"
                    style={{ height: "38px" }}
                    onClick={handleFilterClick}
                  >
                    Filter
                  </button>

                  {/* Reset Button */}
                  <button
                    className="btn btn-icon btn-round btn-dark py-3 px-3"
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    title="Reset Filters"
                    onClick={handleResetFilters}
                  >
                    <i className="fas fa-sync-alt"></i>
                  </button>

                  {/* Add Button */}
                  {role === "admin" && (
                    <button
                      className="btn btn-success rounded-pill px-4"
                      style={{ height: "38px" }}
                      onClick={openAddModal}
                    >
                      <i className="fas fa-plus me-2"></i>
                      Add Daily Record
                    </button>
                  )}
                </div>
              </div>

              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Mode</th>
                        <th>No of Donors</th>
                        <th>Amount</th>
                        <th>Date &amp; Time</th>
                        {role === "admin" && <th>Action</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {records.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center">
                            No Data Found
                          </td>
                        </tr>
                      ) : (
                        records.map((item, index) => (
                          <tr key={item.id}>
                            <td>{(page - 1) * 20 + index + 1}</td>
                            <td>
                              <span
                                className={`badge text-uppercase ${
                                  item.mode?.toLowerCase() === "online"
                                    ? "bg-info text-white"
                                    : "bg-success text-white"
                                }`}
                              >
                                {item.mode}
                              </span>
                            </td>
                            <td>{item.no_of_donors}</td>
                            <td>{formatIndianCurrency(item.amount)}</td>
                            <td>{formatUtcToLocalDate(item.created_at)}</td>
                            {role === "admin" && (
                              <td>
                                <button
                                  className="btn btn-icon btn-round btn-dark btn-sm me-2"
                                  onClick={() => openEditModal(item)}
                                  title="Edit"
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                                <button
                                  className="btn btn-icon btn-round btn-danger btn-sm"
                                  onClick={() => handleDelete(item.id)}
                                  title="Delete"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </td>
                            )}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Pagination identical to Donations.js */}
            <nav aria-label="Page navigation">
              <ul className="pagination flex justify-content-end">
                {/* First Page */}
                <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(1)}
                  >
                    First
                  </button>
                </li>

                {/* Previous */}
                <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(page - 1)}
                  >
                    Previous
                  </button>
                </li>

                {/* Page Number Buttons in chunks */}
                {(() => {
                  const pageNumbers = [];
                  const maxPagesToShow = 5;
                  const startPage =
                    Math.floor((page - 1) / maxPagesToShow) * maxPagesToShow +
                    1;
                  const endPage = Math.min(
                    startPage + maxPagesToShow - 1,
                    totalPages,
                  );

                  for (let i = startPage; i <= endPage; i++) {
                    pageNumbers.push(
                      <li
                        key={i}
                        className={`page-item ${page === i ? "active" : ""}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageClick(i)}
                        >
                          {i}
                        </button>
                      </li>,
                    );
                  }
                  return pageNumbers;
                })()}

                {/* Next */}
                <li
                  className={`page-item ${
                    page === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(page + 1)}
                  >
                    Next
                  </button>
                </li>

                {/* Last Page */}
                <li
                  className={`page-item ${
                    page === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(totalPages)}
                  >
                    Last
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyRegistrations;
